import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import "./LandingPage.css";

export default function LandingPage({ openAuthModal }) {
  const { user, showAuthModal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.openAuth) {
      showAuthModal('signup');
      window.history.replaceState({}, document.title);
    }
  }, []);

  const goToLeads = () => {
    if (user) navigate('/generate-leads');
    else showAuthModal('signup');
  };

  // Typing loop
  const words = ["Owner Phones.", "Restaurant Grades.", "AI Outreach.", "21,000+ Leads."];
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = words[wordIndex];
    const typeSpeed = isDeleting ? 45 : 85;
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setText(currentWord.substring(0, charIndex + 1));
        setCharIndex(p => p + 1);
        if (charIndex + 1 === currentWord.length) {
          setTimeout(() => setIsDeleting(true), 1400);
        }
      } else {
        setText(currentWord.substring(0, charIndex - 1));
        setCharIndex(p => p - 1);
        if (charIndex === 0) {
          setIsDeleting(false);
          setWordIndex(p => (p + 1) % words.length);
        }
      }
    }, typeSpeed);
    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, wordIndex]);

  return (
    <div className="lp-root">

      {/* ── HERO ────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-grid" />
        <div className="lp-hero-glow" />

        <div className="lp-hero-inner">
          
          <h1 className="lp-hero-title">
            NYC restaurant leads with<br />
            <span className="lp-typing">{text}<span className="lp-cursor">|</span></span>
          </h1>

          <p className="lp-hero-sub">
            The only lead tool built exclusively for NYC restaurants. Owner direct lines, restaurant grades, and AI-written outreach — 21,000+ leads ready to go.
          </p>

          <div className="lp-hero-actions">
            <button className="lp-btn-hero" onClick={goToLeads}>
              {user ? 'Try Now ' : 'Try Free Now'}
            </button>
            <div className="lp-hero-proof">
              <span>✓ 10 free leads</span>
              <span>✓ Owner phones included</span>
              <span>✓ Results in 60 seconds</span>
            </div>
          </div>

          <div className="lp-stats-bar">
            {[
              { num: "21,000+", label: "NYC Restaurants" },
              { num: "~72%",    label: "With Owner Phone" },
              { num: "A/B/C",   label: "Restaurant Grades" },
              { num: "< 60s",   label: "Per Search" },
            ].map(s => (
              <div key={s.label} className="lp-stat">
                <div className="lp-stat-num">{s.num}</div>
                <div className="lp-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT YOU GET ────────────────────────────── */}
      <section className="lp-section">
        <div className="lp-section-inner">
          <div className="lp-section-label">What's in every lead</div>
          <h2 className="lp-section-title">Every row in the table<br />is a ready-to-call lead.</h2>

          <div className="lp-data-grid">
            {[
              { icon: "☎️", title: "Owner Direct Line",    desc: "Skip the host stand entirely. Get straight to the person who signs the checks and makes buying decisions.", hot: true  },
              { icon: "🏆", title: "Restaurant Grade",     desc: "A, B, or C rating for every restaurant. Grade B/C restaurants signal unmet needs — your highest-priority prospects.", hot: false },
              { icon: "📞", title: "Business Phone",       desc: "The public-facing number for every restaurant. Call ready, click to dial.", hot: false },
              { icon: "✉️", title: "Email Address",        desc: "Direct email where available. Export and drop into any outreach sequence.", hot: false },
              { icon: "🌐", title: "Website",              desc: "Direct link to their site. No website listed = an immediate sales opportunity.", hot: false },
              { icon: "🤖", title: "AI Outreach",          desc: "Tell the AI what you sell. It writes a personalised cold email and phone script for each restaurant.", hot: true  },
            ].map(d => (
              <div key={d.title} className={`lp-data-card${d.hot ? ' lp-data-card-hot' : ''}`}>
                {d.hot && <div className="lp-data-hot-badge">Unique</div>}
                <div className="lp-data-icon">{d.icon}</div>
                <div className="lp-data-title">{d.title}</div>
                <div className="lp-data-desc">{d.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────── */}
      <section className="lp-section lp-section-dark">
        <div className="lp-section-inner lp-hiw-inner">

          {/* LEFT — text content */}
          <div className="lp-hiw-left">
            <div className="lp-section-label">How it works</div>
            <h2 className="lp-section-title lp-hiw-title">Three steps.<br />Under 60 seconds.</h2>

            <div className="lp-steps">
              {[
                { n: "01", title: "Click Search",      desc: "One button. NYLeads instantly surfaces thousands of NYC restaurant leads — fresh, verified, and ready to work." },
                { n: "02", title: "Get Fresh Leads",   desc: "Every search returns leads you haven't seen before. Owner phones, restaurant grades, emails, and websites — all in one place." },
                { n: "03", title: "AI Does the Rest",  desc: "Tell the AI what you sell. It filters your best prospects and writes a personalised outreach script for each one." },
              ].map((s, i) => (
                <div key={s.n} className="lp-step">
                  <div className="lp-step-num">{s.n}</div>
                  <div className="lp-step-content">
                    <div className="lp-step-title">{s.title}</div>
                    <div className="lp-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — demo video (single wrapper, no inner div) */}
          <div className="lp-hiw-right">
            <div className="lp-video-frame">
              <video
                src="https://res.cloudinary.com/dedjvyrhn/video/upload/v1774681756/NYLeads_Demo_kf1wv4.mp4"
                autoPlay
                muted
                loop
                playsInline
                controls
              />
            </div>
          </div>

        </div>
      </section>

      {/* ── THE MOAT ────────────────────────────────── */}
      <section className="lp-section">
        <div className="lp-section-inner lp-moat-inner">
          <div className="lp-moat-left">
            <div className="lp-section-label">The data moat</div>
            <h2 className="lp-section-title">No other tool has<br />the owner's direct line.</h2>
            <p className="lp-moat-desc">
              Most lead tools give you the same thing: a front-of-house number and a generic email. You call, you get the host stand, you get ignored. NYLeads is built differently — we spent months aggregating and cross-referencing data that no one else bothered to compile.
            </p>
            <p className="lp-moat-desc">
              The result is a direct line to the actual decision maker on roughly 55% of all leads. Not a receptionist. Not a manager on shift. The owner. That one difference changes every conversation you have.
            </p>
            <button className="lp-btn-hero" style={{ marginTop: 28 }} onClick={goToLeads}>
              {user ? 'Search Now →' : 'Try It Free →'}
            </button>
          </div>
          <div className="lp-moat-right">
            <div className="lp-compare">
              <div className="lp-compare-head">
                <span>Data point</span>
                <span className="lp-compare-us">NYLeads</span>
                <span className="lp-compare-them">Others</span>
              </div>
              {[
                ["Owner direct phone",    true,  false],
                ["Restaurant grade",      true,  false],
                ["DBA / LLC name",        true,  false],
                ["Business phone",        true,  true ],
                ["Email",                 true,  true ],
                ["Website",               true,  true ],
                ["AI outreach scripts",   true,  false],
                ["NYC coverage",          true,  true ],
              ].map(([label, us, them]) => (
                <div key={label} className="lp-compare-row">
                  <span className="lp-compare-label">{label}</span>
                  <span className={`lp-compare-val ${us ? 'yes' : 'no'}`}>{us ? '✓' : '✗'}</span>
                  <span className={`lp-compare-val ${them ? 'yes' : 'no'}`}>{them ? '✓' : '✗'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────── */}
      <section className="lp-section lp-section-dark" id="pricing">
        <div className="lp-section-inner">
          <div className="lp-section-label">Pricing</div>
          <h2 className="lp-section-title">Pay for what you use.<br />No surprises.</h2>

          <div className="lp-pricing-grid">
            {[
              {
                plan:     "Free",
                price:    "$0",
                per:      "",
                leads:    "10 leads",
                features: ["Business phone + email", "Restaurant grade A/B/C", "AI outreach scripts", "CSV export"],
                cta:      "Get Started",
                featured: false,
              },
              {
                plan:     "Starter",
                price:    "$29",
                per:      "/mo",
                leads:    "300 leads/mo",
                features: ["Everything in Free", "Owner direct phones", "No duplicate leads", "Search history"],
                cta:      "Get Starter",
                featured: false,
              },
              {
                plan:     "Pro",
                price:    "$79",
                per:      "/mo",
                leads:    "1,000 leads/mo",
                features: ["Everything in Starter", "Bulk AI scripts", "Priority support", "Early access"],
                cta:      "Get Pro",
                featured: true,
              },
              {
                plan:     "Agency",
                price:    "$199",
                per:      "/mo",
                leads:    "3,000 leads/mo",
                features: ["Everything in Pro", "3,000 leads/month", "Dedicated support", "Custom onboarding"],
                cta:      "Get Agency",
                featured: false,
              },
            ].map(p => (
              <div key={p.plan} className={`lp-price-card${p.featured ? ' lp-price-card-featured' : ''}`}>
                {p.featured && <div className="lp-price-badge">Most Popular</div>}
                <div className="lp-price-plan">{p.plan}</div>
                <div className="lp-price-amount">
                  {p.price}<span className="lp-price-per">{p.per}</span>
                </div>
                <div className="lp-price-leads">{p.leads}</div>
                <ul className="lp-price-features">
                  {p.features.map(f => <li key={f}>{f}</li>)}
                </ul>
                <button
                  className={`lp-price-btn${p.featured ? ' lp-price-btn-featured' : ''}`}
                  onClick={goToLeads}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────── */}
      <section className="lp-cta">
        <div className="lp-cta-glow" />
        <div className="lp-cta-inner">
          <h2 className="lp-cta-title">Ready to call the owner<br />instead of the host stand?</h2>
          <p className="lp-cta-sub">10 free leads. No credit card. Results in 60 seconds.</p>
          <button className="lp-btn-hero lp-btn-hero-lg" onClick={goToLeads}>
            {user ? 'Try Now ' : 'Start Free Now →'}
          </button>
        </div>
      </section>

    </div>
  );
}